// Hook para regenerar el signup_token de un paciente que todavía no
// completó signup. Wrap de RPC regenerar_invitacion (migration 0009).

import { useState } from "react";
import { supabase } from "../client";

export interface RegenerarInvitacionResult {
  signupToken: string;
  expiresAt: string;
}

export interface UseRegenerarInvitacionReturn {
  regenerar: (pacienteId: string) => Promise<RegenerarInvitacionResult | null>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

export function useRegenerarInvitacion(): UseRegenerarInvitacionReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function regenerar(
    pacienteId: string,
  ): Promise<RegenerarInvitacionResult | null> {
    setLoading(true);
    setError(null);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "regenerar_invitacion",
        { p_paciente_id: pacienteId },
      );

      if (rpcError) {
        console.warn("[useRegenerarInvitacion] rpc error", rpcError);
        setError(rpcError.message);
        return null;
      }

      if (rpcData && !rpcData.ok) {
        const code = rpcData.code as string;
        const msg =
          code === "ALREADY_ACTIVE"
            ? "Este paciente ya activó su cuenta — no se puede regenerar invitación."
            : code === "NOT_FOUND"
              ? "No encontramos el paciente."
              : code === "NO_AUTH"
                ? "Tu sesión expiró. Iniciá sesión de nuevo."
                : (rpcData.message as string | undefined) ??
                  "No pudimos regenerar la invitación.";
        setError(msg);
        return null;
      }

      return {
        signupToken: rpcData.signup_token as string,
        expiresAt: rpcData.expires_at as string,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useRegenerarInvitacion] failed", e);
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { regenerar, loading, error, reset: () => setError(null) };
}
